import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { errors, fields, personObj } from '../../../constants';
import { personEntityTitle } from '../../../utils/helper';
import { isDesignatedContactComplete } from '../../../utils/completes';
import { isEmail, isPhoneWithCountry } from '../../../utils/validations';
import { ProfileContext } from '../../../context/Profile.context';
import { affilations } from '../../../constants/companyAffilations';
import { OTHER_PLACEHOLDER } from '../../../constants';
import { Lock, Trash2, User, UserCheck } from 'lucide-react';
import Controls from '../../Controls';
import { PhoneInputField } from '../../PhoneInputField';
import PrivateInformation from '../../PrivateInformation/PrivateInformation';
import PreviousAdded from '../../PreviousAdded';
import RemovePerson from '../../RemovePerson';
import { Box, Button, Dialog, DialogContent, DialogTitle, EntityLabel, EntityLabelGroup, Label, InfoPanel, SectionTitle, SelectField, TextField, Typography } from '@otcmarketsgroup/otcm-ui-lib';

const CONTACT_PARAM = fields.PRIMARY_CONTACT;
const PREV_ADDED_VIEW = 'previousAdded';
const FORM_VIEW = 'formView';
const REMOVE_PERSON_VIEW = 'removePerson';

const PrimaryContactsTab = ({ onPersonAdd, onPersonUpdate }) => {
  const [profileState] = useContext(ProfileContext);
  const [contactView, setContactView] = useState(personObj);
  const [dialog, setDialog] = useState(false);
  const [viewHistory, setViewHistory] = useState(null);
  const [validationError, setValidationError] = useState(false);
  const peopleList = profileState.profile.peopleList;
  const primaryContactList = peopleList.filter(person => person[CONTACT_PARAM]);
  const prevAddedList = peopleList.filter(person => !person[CONTACT_PARAM] && (person.isOfficer || person.isDirector || person.isAuthorizedUser || person.isPrimaryBillingContact || person.isOtherBillingContact));
  const previousAddView = !contactView.id && prevAddedList && prevAddedList.length > 0;
  const contactTotal = peopleList.filter(person => person[CONTACT_PARAM]).length;
  const [slideView, setSlideView] = useState(previousAddView ? PREV_ADDED_VIEW : FORM_VIEW);
  const contactTotalTitle = `${contactTotal} out of 3 Primary Contacts`;
  const isExistingContact = contactView && (contactView.isOfficer || contactView.isDirector);
  const isAdd = !contactView.id || (contactView && !contactView[CONTACT_PARAM]);
  const isReadOnly = profileState.readOnly;
  const isRemoveView = slideView === REMOVE_PERSON_VIEW;
  const dialogTitle = isRemoveView ? `Remove - ${personEntityTitle(contactView, false)}` : isAdd ? 'Add Primary Contact' : personEntityTitle(contactView, false);

  useEffect(() => {
    if (!dialog) {
      setContactView(personObj);
      setSlideView(previousAddView ? PREV_ADDED_VIEW : FORM_VIEW);
      setValidationError(false);
    }
  }, [dialog, previousAddView]);

  const onFieldChange = (field, value) => {
    setContactView({
      ...contactView,
      [field]: value
    });
  };

  const onPhoneValueChanged = (phone, data) => {
    setContactView({
      ...contactView,
      phone,
      phoneCountryCode: data?.countryCode ? data.countryCode.toUpperCase() : contactView?.phoneCountryCode
    });
  };

  const onAffiliationChange = value => {
    setContactView({
      ...contactView,
      title1: value === OTHER_PLACEHOLDER ? OTHER_PLACEHOLDER : value
    });
  };

  const handleEdit = person => {
    setSlideView(FORM_VIEW);
    setContactView({ ...person });
    setValidationError(!isDesignatedContactComplete(person, CONTACT_PARAM));
    setDialog(true);
  };

  const handleCancelRemove = () => {
    setDialog(false);
  };

  const handleRemovePerson = person => {
    setContactView(person);
    setSlideView(REMOVE_PERSON_VIEW);
    setDialog(true);
  };

  const onRemoveSubmit = person => {
    onPersonUpdate(person);
    setDialog(false);
  };

  const validateComplete = person => {
    return isDesignatedContactComplete(person, CONTACT_PARAM);
  };

  const handleCancelView = () => {
    viewHistory === PREV_ADDED_VIEW && prevAddedList && prevAddedList.length > 0 ? setSlideView(PREV_ADDED_VIEW) : setDialog(false);
    setContactView(personObj);
  };

  const handleSubmitPerson = () => {
    setValidationError(false);

    const contact = contactView;
    const onSubmit = !contact.id ? onPersonAdd : onPersonUpdate;

    contact[CONTACT_PARAM] = true;

    if (!isDesignatedContactComplete(contact, CONTACT_PARAM)) {
      setValidationError(true);
      return;
    }

    onSubmit(contact);
    setDialog(false);
  };

  const handleSelectPrevContact = person => {
    setViewHistory(PREV_ADDED_VIEW);
    setContactView(person);
    setSlideView(FORM_VIEW);
  };

  const handleAddNew = () => {
    setViewHistory(PREV_ADDED_VIEW);
    setSlideView(FORM_VIEW);
  };

  const renderPreviouslyAdded = () => (
    <>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='body1' >
            Select an individual previously entered, or Add a New Primary Contact.
          </Typography>
          <PreviousAdded
            listTitle='List of Individuals Previously Entered'
            items={prevAddedList}
            handleSelect={handleSelectPrevContact} />
          <Button
            fullWidth
            onClick={handleAddNew}>
            Add New Primary Contact
          </Button>
        </Box>
      </DialogContent>
      <Controls
        cancelText='Cancel'
        showSubmit={false}
        onCancelClick={() => setDialog(false)} />
    </>
  );

  const dialogClose = () => {
    setDialog(false);
  };

  const renderAddNew = () => {
    const isPresetAffiliation = affilations.some(item => item.value === contactView?.title1);
    const showAffiliationInput = !isExistingContact && (contactView?.title1 === OTHER_PLACEHOLDER || (contactView?.title1 && !isPresetAffiliation));
    const affiliationMissing = validationError && !isExistingContact && !contactView?.title1;
    const emailInvalid = validationError && !!(contactView?.email && !isEmail(contactView?.email));
    const emailMissing = validationError && !contactView?.email;
    const phoneBlank = validationError && (!contactView?.phone || !contactView?.phoneCountryCode);
    const phoneInvalid = validationError && !!(contactView?.phone && contactView?.phoneCountryCode && !isPhoneWithCountry(contactView?.phone, contactView?.phoneCountryCode));
    const phoneErrorText = phoneBlank ? errors.INCOMPLETE_DATA_FIELD : phoneInvalid ? errors.INVALID_PHONE : '';
    return <>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <PrivateInformation />
          {!isExistingContact && <>
            <Typography variant='body1'>
              Enter title and name of Primary Contact.
            </Typography>
            <Box display='flex' flexDirection='column' gap={2}>
              <TextField
                label='First Name'
                value={contactView?.firstName || ''}
                placeholder='Enter text here'
                showClearButton
                readOnly={isReadOnly}
                maxLength={75}
                showClearButton
                error={(validationError || isReadOnly) && !contactView?.firstName}
                helperText={(validationError || isReadOnly) && !contactView?.firstName ? errors.INCOMPLETE_DATA_FIELD : ''}
                onChange={e => onFieldChange('firstName', e.target.value)}
              />
              <TextField
                label='Middle Name'
                value={contactView?.middleName || ''}
                placeholder='Enter text here'
                showClearButton
                readOnly={isReadOnly}
                maxLength={75}
                showClearButton
                onChange={e => onFieldChange('middleName', e.target.value)}
              />
              <TextField
                label='Last Name'
                value={contactView?.lastName || ''}
                placeholder='Enter text here'
                showClearButton
                readOnly={isReadOnly}
                maxLength={75}
                showClearButton
                error={(validationError || isReadOnly) && !contactView?.lastName}
                helperText={(validationError || isReadOnly) && !contactView?.lastName ? errors.INCOMPLETE_DATA_FIELD : ''}
                onChange={e => onFieldChange('lastName', e.target.value)}
              />
            </Box>
          </>}
          <Typography variant='body1'>
            Enter phone number and email for Primary Contact.
          </Typography>
          <Box display='flex' flexDirection='column' gap={2}>
            <Box>
              <Label startIcon={<Lock size='16px' color='var(--otc-green)' />}>
                Phone Number
              </Label>
              <PhoneInputField
                value={contactView?.phone || ''}
                country={contactView?.phoneCountryCode?.toLowerCase()}
                error={phoneBlank || phoneInvalid}
                errorText={phoneErrorText}
                onChange={onPhoneValueChanged}
              />
            </Box>
            <Box>
              <Label startIcon={<Lock size='16px' color='var(--otc-green)' />}>
                Email
              </Label>
              <TextField
                value={contactView?.email || ''}
                placeholder='Enter text here'
                maxLength={75}
                showClearButton
                readOnly={isReadOnly}
                error={emailMissing || emailInvalid}
                helperText={emailMissing ? errors.INCOMPLETE_DATA_FIELD : emailInvalid ? errors.INVALID_EMAIL : ''}
                onChange={e => onFieldChange('email', e.target.value)}
              />
            </Box>
            {!isExistingContact && <>
              <SelectField
                label='Company Affiliation'
                value={contactView?.title1 ? (isPresetAffiliation ? contactView.title1 : OTHER_PLACEHOLDER) : ''}
                options={affilations}
                readOnly={isReadOnly}
                error={affiliationMissing}
                helperText={affiliationMissing ? errors.INCOMPLETE_DATA_FIELD : ''}
                onChange={e => onAffiliationChange(e.target.value)}
              />
              {showAffiliationInput && <TextField
                label='Enter other affiliation'
                value={contactView?.title1 === OTHER_PLACEHOLDER ? '' : (contactView?.title1 || '')}
                readOnly={isReadOnly}
                placeholder='Enter text here'
                showClearButton
                maxLength={75}
                error={(validationError || isReadOnly) && contactView?.title1 === OTHER_PLACEHOLDER}
                helperText={(validationError || isReadOnly) && contactView?.title1 === OTHER_PLACEHOLDER ? errors.INCOMPLETE_DATA_FIELD : ''}
                onChange={e => onFieldChange('title1', e.target.value)}
              />}
            </>}
          </Box>
          {validationError && <InfoPanel isWarning title={errors.INCOMPLETE_DATA} />}
        </Box>
      </DialogContent>
      {!isReadOnly && <Controls
        cancelText='Cancel'
        submitText={isAdd ? 'Add' : 'Update'}
        onCancelClick={handleCancelView}
        onSubmitClick={handleSubmitPerson}
      />}
    </>;
  };

  return (
    <div>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {contactTotal < 1 && <InfoPanel isWarning title='Incomplete Information. Need at least 1 complete Primary Contact.' />}
        <Typography variant='body1'>
          Designate up to three primary contacts to respond to inquiries regarding this application and to receive important OTCQX U.S. communications, including compliance notices.
        </Typography>
        <Box>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <SectionTitle noUnderline>{contactTotalTitle}</SectionTitle>
            {(contactTotal < 3 && !isReadOnly) && <Button variant='text' onClick={() => setDialog(true)}>
              Add Primary Contact
            </Button>}
          </Box>

          {primaryContactList.length === 0 && <Typography variant='body2'>
            No Primary Contacts have been added yet.
          </Typography>}

          <EntityLabelGroup>
            {primaryContactList.map((person, index) => {
              const complete = validateComplete(person);

              return <EntityLabel
                key={`primary-${person.id || index}`}
                title={personEntityTitle(person, false)}
                subtitle={isReadOnly ? 'View' : complete ? 'Complete' : 'Enter Information'}
                icon={complete ? <UserCheck /> : <User />}
                actionIcon={<Trash2 />}
                isComplete={complete}
                isWarning={!complete}
                onEntityClick={() => handleEdit(person)}
                onActionClick={!isReadOnly ? () => handleRemovePerson(person) : undefined} />;
            })}
          </EntityLabelGroup>
        </Box>
      </Box>
      <Dialog open={dialog} onClose={dialogClose}>
        <DialogTitle title={dialogTitle} onClose={dialogClose} />
        {slideView === PREV_ADDED_VIEW && renderPreviouslyAdded()}
        {slideView === FORM_VIEW && renderAddNew()}
        {isRemoveView && <RemovePerson
          person={{ ...contactView }}
          selectedRemove={CONTACT_PARAM}
          onCancel={handleCancelRemove}
          onRemoveSubmit={onRemoveSubmit} />}
      </Dialog>
    </div>
  );
};

PrimaryContactsTab.propTypes = {
  onPersonAdd: PropTypes.func,
  onPersonUpdate: PropTypes.func
};

export default PrimaryContactsTab;
